import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { EmailOtpType } from "@supabase/supabase-js";
import { supabase } from "../../lib/supabaseClient";

const NAVY = "#2a2420";
const ORANGE = "#607840";

type ConfirmState = "checking" | "success" | "error";

function getHashParams() {
  return new URLSearchParams(window.location.hash.replace(/^#/, ""));
}

export default function AuthConfirmPage() {
  const navigate = useNavigate();
  const [state, setState] = useState<ConfirmState>("checking");
  const [message, setMessage] = useState("Confirming your email...");

  const queryParams = useMemo(() => new URLSearchParams(window.location.search), []);

  useEffect(() => {
    let alive = true;

    async function confirmEmail() {
      const hashParams = getHashParams();
      const hashError = hashParams.get("error_description") || hashParams.get("error");

      if (hashError) {
        if (!alive) return;
        setState("error");
        setMessage(hashError.replace(/\+/g, " "));
        return;
      }

      const tokenHash = queryParams.get("token_hash");
      const type = (queryParams.get("type") || "email") as EmailOtpType;

      if (tokenHash) {
        const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
        if (!alive) return;

        if (error) {
          setState("error");
          setMessage(error.message);
          return;
        }

        setState("success");
        setMessage("Your email has been confirmed.");
        window.setTimeout(() => navigate("/gate", { replace: true }), 1400);
        return;
      }

      const { data } = await supabase.auth.getSession();
      if (!alive) return;

      if (data.session) {
        setState("success");
        setMessage("Your email has been confirmed.");
        window.setTimeout(() => navigate("/gate", { replace: true }), 1400);
        return;
      }

      setState("success");
      setMessage("Your email has been confirmed. You can sign in now.");
    }

    confirmEmail();

    return () => {
      alive = false;
    };
  }, [navigate, queryParams]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-10" style={{ backgroundColor: "#fefcf8" }}>
      <div className="w-full max-w-sm text-center">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5"
          style={{
            backgroundColor:
              state === "error" ? "rgba(220,38,38,0.08)" : "rgba(16,185,129,0.1)",
          }}
        >
          {state === "checking" && <Loader2 size={28} className="animate-spin" style={{ color: ORANGE }} />}
          {state === "success" && <CheckCircle size={28} style={{ color: "#059669" }} />}
          {state === "error" && <XCircle size={28} style={{ color: "#dc2626" }} />}
        </div>

        <h1 className="text-xl font-extrabold mb-2" style={{ color: NAVY }}>
          {state === "checking" && "Confirming email"}
          {state === "success" && "Email confirmed"}
          {state === "error" && "Confirmation failed"}
        </h1>

        <p className="text-sm leading-relaxed" style={{ color: NAVY, opacity: 0.6 }}>
          {message}
        </p>

        {state === "success" && (
          <Link to="/gate" className="inline-block mt-6 text-sm font-semibold" style={{ color: ORANGE }}>
            Continue
          </Link>
        )}

        {state === "error" && (
          <Link to="/signup" className="inline-block mt-6 text-sm font-semibold" style={{ color: ORANGE }}>
            Back to sign up
          </Link>
        )}
      </div>
    </div>
  );
}
